import {FormNameEnum} from 'enums/form';
import {requireAllRequiredResourceFields, requireAllRequiredResourceFieldsDate} from './constants';

export interface IRequiredResourceFieldConfig {
  /** Form field name */
  field: FormNameEnum;
  /**
   * true = required before the new required-fields rollout (legacy).
   * false = newly added required field (only enforced for new records / when flag is on /
   * records created on or after requireAllRequiredResourceFieldsDate).
   */
  originalRequiredField: boolean;
}

/**
 * Registry of required resource upload/edit fields.
 * Add new required fields here with originalRequiredField: false.
 */
export const requiredResourceFields: IRequiredResourceFieldConfig[] = [
  {field: FormNameEnum.file, originalRequiredField: true},
  {field: FormNameEnum.fileType, originalRequiredField: true},
  {field: FormNameEnum.displayName, originalRequiredField: true},
  {field: FormNameEnum.languages, originalRequiredField: true},
  {field: FormNameEnum.isAccessibleToStudents, originalRequiredField: true},
  {field: FormNameEnum.hasUploadPermissions, originalRequiredField: true},
  {field: FormNameEnum.webLink, originalRequiredField: true},
  {field: FormNameEnum.link, originalRequiredField: false},
  {field: FormNameEnum.programs, originalRequiredField: false},
  {field: FormNameEnum.briefDescription, originalRequiredField: false},
  {field: FormNameEnum.keyTerms, originalRequiredField: false},
];

export const getRequiredResourceFieldConfig = (field: FormNameEnum): IRequiredResourceFieldConfig | undefined =>
  requiredResourceFields.find((entry) => entry.field === field);

/**
 * New records (no createdUtc) always enforce every required field.
 * Edit: if requireAllRequiredResourceFields is false and createdUtc is before the cutoff date,
 * only originalRequiredField fields are required; otherwise all required fields are.
 */
export const shouldEnforceAllRequiredResourceFields = (createdUtc?: string | null): boolean => {
  if (!createdUtc) {
    return true;
  }

  if (requireAllRequiredResourceFields) {
    return true;
  }

  const createdDate = getCreatedDateOnly(createdUtc);
  if (!createdDate) {
    // Unknown format — treat as legacy-safe and do not force new required fields.
    return false;
  }

  return createdDate >= requireAllRequiredResourceFieldsDate;
};

/** Normalize API createdUtc values to YYYY-MM-DD for cutoff comparison. */
const getCreatedDateOnly = (createdUtc: string): string | null => {
  const trimmed = createdUtc.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10);
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString().slice(0, 10);
};

export const isResourceFieldRequired = (originalRequiredField: boolean, createdUtc?: string | null): boolean => {
  if (shouldEnforceAllRequiredResourceFields(createdUtc)) {
    return true;
  }

  return originalRequiredField;
};

export const isRegisteredResourceFieldRequired = (field: FormNameEnum, createdUtc?: string | null): boolean => {
  const config = getRequiredResourceFieldConfig(field);
  if (!config) {
    return false;
  }

  return isResourceFieldRequired(config.originalRequiredField, createdUtc);
};
